import { memo, useState } from "react";
import { CheckIcon, ClipboardDocumentIcon, success } from "@";
import ColorBox from "./ColorBox";

// A swatch that copies its color code on click. Lives inside the radio
// <label>, so the click must not also toggle the theme selection.
const CopyColorSwatch = memo(({ color }) => {
  const [copied, setCopied] = useState(false);
  if (!color) return null;

  const raw = color.trim();
  const code = raw.startsWith("#") ? raw.toUpperCase() : raw;

  const handleCopy = async (e) => {
    e.preventDefault(); // don't let the <label> flip the radio
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      success(`تم نسخ اللون ${code}`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — ignore silently */
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`نسخ ${code}`}
      aria-label={`نسخ كود اللون ${code}`}
      className="group/swatch relative inline-flex cursor-pointer items-center justify-center rounded-lg outline-none transition-transform duration-150 hover:scale-105 focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-700"
    >
      <ColorBox color={color} size="h-7 w-7" />

      {/* Copy / copied overlay — a scrim keeps the glyph legible on any color */}
      <span
        className={`pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg transition-opacity duration-150 ${
          copied
            ? "bg-black/40 opacity-100"
            : "bg-black/30 opacity-0 group-hover/swatch:opacity-100"
        }`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-white" />
        )}
      </span>
    </button>
  );
});

CopyColorSwatch.displayName = "CopyColorSwatch";

const ColorGroup = ({ input, checked, value, label }) => {
  const [colorA, colorB, colorC] = value?.split(",");

  return (
    <label
      className={`group/theme relative block cursor-pointer overflow-hidden rounded-2xl border bg-white p-3 transition-all duration-200 dark:bg-gray-700 ${
        checked
          ? "border-purple-300 shadow-sm shadow-purple-900/10 ring-2 ring-purple-200 dark:border-purple-600/70 dark:ring-purple-700/40"
          : "border-gray-100 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-sm dark:border-gray-600 dark:hover:border-purple-700"
      }`}
    >
      <input
        type="radio"
        className="peer sr-only"
        {...input}
        value={value}
        checked={checked}
        readOnly
      />

      {/* Theme preview banner */}
      <div className="relative mb-3 flex h-10 overflow-hidden rounded-2xl ring-1 ring-black/5 dark:ring-white/10">
        <span style={{ backgroundColor: colorA }} className="flex-1" />
        <span style={{ backgroundColor: colorB }} className="flex-1" />
        <span style={{ backgroundColor: colorC }} className="flex-1" />
        {/* Glossy overlay */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent"
        />
        {/* Active check badge */}
        {checked && (
          <span className="absolute end-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white text-purple-900 shadow-md ring-2 ring-white dark:bg-gray-900 dark:text-purple-300 dark:ring-gray-800">
            <CheckIcon className="h-4 w-4" strokeWidth={3} />
          </span>
        )}
      </div>

      {/* Color swatches — click any swatch to copy its hex code */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <CopyColorSwatch color={colorA} />
          <CopyColorSwatch color={colorB} />
          <CopyColorSwatch color={colorC} />
        </div>
        {label && (
          <span
            className={`text-xs font-medium tracking-tight ${
              checked
                ? "text-purple-900 dark:text-purple-300"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {label}
          </span>
        )}
      </div>
    </label>
  );
};

export default ColorGroup;
